/**
 * KlaviyoService – fires "Selected Vehicle" custom events via window._learnq
 * when a user completes vehicle selection (YMM, plus trim/cab when required).
 *
 * Requires the Klaviyo onsite script on the page (theme.liquid).
 * Disable with: window.scs_klaviyo_vehicle_tracking = false
 */

declare global {
  interface Window {
    _learnq?: unknown[];
    scs_klaviyo_vehicle_tracking?: boolean;
  }
}

export interface SelectedVehicleProps {
  year: string;
  make: string;
  model: string;
  trim?: string;
  cab?: string;
  has_trims?: boolean;
  has_cabs?: boolean;
  source?: string;
}

const EVENT_NAME = 'Selected Vehicle';

let lastTrackedKey = '';

function isEnabled(): boolean {
  return window.scs_klaviyo_vehicle_tracking !== false;
}

function learnq(): unknown[] {
  window._learnq = window._learnq || [];
  return window._learnq;
}

export function buildVehicleFullYmm(year: string, make: string, model: string): string {
  return [year, make, model].join(' ');
}

export function buildVehicleFullYmmtc(
  year: string,
  make: string,
  model: string,
  trim?: string,
  cab?: string,
): string {
  return [year, make, model, trim, cab].filter(Boolean).join(' ');
}

export function isYmmtcComplete(props: SelectedVehicleProps): boolean {
  const { year, make, model, trim, cab, has_trims, has_cabs } = props;
  if (!year || !make || !model) return false;
  if (has_trims && !trim) return false;
  if (has_cabs && !cab) return false;
  return true;
}

function buildVehicleKey(props: SelectedVehicleProps): string {
  const { year, make, model, trim, cab } = props;
  return [year, make, model, trim ?? '', cab ?? ''].join('|');
}

export function trackSelectedVehicle(props: SelectedVehicleProps): void {
  if (!isEnabled() || !isYmmtcComplete(props)) return;

  const { year, make, model, trim, cab, source } = props;
  const key = buildVehicleKey(props);
  if (key === lastTrackedKey) return;
  lastTrackedKey = key;

  const vehicleFull = buildVehicleFullYmm(year, make, model);
  const vehicleFullYmmtc = buildVehicleFullYmmtc(year, make, model, trim, cab);

  const eventProps: Record<string, string> = {
    Vehicle_Year: year,
    Vehicle_Make: make,
    Vehicle_Model: model,
    Vehicle_Full: vehicleFull,
    Vehicle_Full_YMMTC: vehicleFullYmmtc,
    Page_URL: window.location.href,
    Source: source ?? 'ymm_selector',
  };

  if (trim) eventProps.Vehicle_Trim = trim;
  if (cab) eventProps.Vehicle_Cab = cab;

  learnq().push(['track', EVENT_NAME, eventProps]);

  learnq().push([
    'identify',
    {
      Vehicle_Year: year,
      Vehicle_Make: make,
      Vehicle_Model: model,
      Vehicle_Full: vehicleFull,
      Vehicle_Full_YMMTC: vehicleFullYmmtc,
      ...(trim ? { Vehicle_Trim: trim } : {}),
      ...(cab ? { Vehicle_Cab: cab } : {}),
    },
  ]);
}

/** @internal Test-only reset */
export function _resetForTest(): void {
  lastTrackedKey = '';
}
